import { mdiTrayArrowDown } from "@mdi/js";
import Icon from "@mdi/react";
import { Tooltip } from "@mui/material";
import { format } from "date-fns";
import _ from "lodash";
import React from "react";
import { CSVLink } from "react-csv";
import { Row } from "react-table";
import { TooltipTransitionComponent } from "../../../base";
import { renderAmount, renderDate, renderDateTime } from "../cells/cells";
import * as S from './table-export.styles';

// Helper function to format cell value for CSV export
const formatCellValue = (value: any, column: any): string => {
    // Handle null/undefined
    if (value === null || value === undefined) {
        return '';
    }

    // Handle Amount objects (has amount and currency properties)
    if (_.isObject(value) && 'amount' in value && typeof (value as any).amount === 'string') {
        return renderAmount(value as any);
    }

    // Handle date strings - check if it's a valid date string
    if (_.isString(value) && (value.match(/^\d{4}-\d{2}-\d{2}/) || value.match(/^\d{4}-\d{2}-\d{2}T/))) {
        // Try to determine if it's date or datetime based on format
        if (value.includes('T') || value.includes(' ')) {
            return renderDateTime(value);
        } else {
            return renderDate(value);
        }
    }

    // Handle arrays
    if (_.isArray(value)) {
        return value.map(v => formatCellValue(v, column)).join(', ');
    }

    // Handle plain objects - try to extract meaningful string
    if (_.isObject(value) && !_.isDate(value)) {
        // If object has a toString method, use it
        if (typeof value.toString === 'function' && value.toString() !== '[object Object]') {
            return value.toString();
        }
        // Otherwise, try to stringify meaningfully
        if ('name' in value) {
            return String(value.name);
        }
        if ('label' in value) {
            return String(value.label);
        }
        if ('id' in value) {
            return String(value.id);
        }
        // Last resort: JSON stringify
        return JSON.stringify(value);
    }

    // Handle booleans
    if (_.isBoolean(value)) {
        return value ? 'Yes' : 'No';
    }

    // Return string representation for primitives
    return String(value);
};

const getColumnHeaderLabel = (column: any): string | undefined => {
    const header = column?.Header;

    if (_.isString(header)) {
        return header;
    }

    if (React.isValidElement(header) && _.isString((header as any).props?.children)) {
        return (header as any).props.children;
    }

    if (column?.id) {
        return _.startCase(String(column.id));
    }

    return undefined;
};

export const TableExport: React.FC<{ rows: Row<any>[], prepareRow: (row: Row<any>) => void, filename?: string }> = ({ rows, prepareRow, filename = 'export' }) => {
    const [data, setData] = React.useState<object[]>([]);

    const getData = () => {
        const parsedData = rows.map(row => {
            prepareRow(row);
            return row.cells.reduce((acc, cell) => {
                const headerLabel = getColumnHeaderLabel(cell.column);
                if (!headerLabel) {
                    return acc;
                }

                if (cell.column.id === 'Selected' || cell.column.id === 'selection' || cell.column.id === 'Actions') {
                    return acc;
                }

                acc[headerLabel] = formatCellValue(cell.value, cell.column);
                return acc;
            }, {})
        });

        setData(parsedData);
    }

    // Ensure filename has .csv extension
    const csvFilename = filename.endsWith('.csv') ? filename : `${filename}.csv`;

    return <S.Container>
        <Tooltip
            placement="top-start"
            title={`Export the table to CSV. Only visible data will be exported.`}
            TransitionComponent={TooltipTransitionComponent}>
            <div>
                <CSVLink data={data} onClick={getData} filename={csvFilename}>
                    <S.ActionButton variant="outlined" size="small" type="button">
                        <Icon path={mdiTrayArrowDown} size={0.7} />
                    </S.ActionButton>
                </CSVLink>
            </div>
        </Tooltip>
    </S.Container >
}